import React, { useState } from 'react';
import { Box, Chip, Skeleton, Stack, Tooltip } from '@mui/material';
import styles from './styles';
import IconMi from 'components/common/icon/IconMi';
import { useQuery } from '@tanstack/react-query';
import { APIResponse } from 'tools/types';
import { editorTopicsAPI, TopicsInfo } from 'api/editor/topics';
import IconHandler from 'components/common/icon/IconHandler';
import { openInNewTab } from 'tools/tools';
import { useHomeContext } from 'app/home/context';
import { ITileInfoTopics } from '../types';

export default function InfoTopics({ tile, preloadedData }: { tile: ITileInfoTopics; preloadedData?: TopicsInfo }) {
    const [collapseTermSectionList, setCollapseTermSectionList] = useState<any>({});
    const ctx = useHomeContext();
    const [topicPopupProps] = [ctx.topicPopupProps];

    const { data: uploadedData, isLoading } = useQuery<APIResponse<TopicsInfo> | null, Error>({
        queryKey: [`tile-${tile.element_id}-topics`],
        queryFn: () => {
            if (!preloadedData && tile.topics && tile.topics.length > 0) {
                return editorTopicsAPI.getTopicsInfo(tile.topics);
            }

            return null;
        },
    });

    const data = preloadedData ? preloadedData : (uploadedData?.data ?? null);

    const onTagClick = (isTag: boolean, tagId: number) => {
        if (isTag) {
            openInNewTab(`/search/global?tag=${tagId}`);
        } else {
            topicPopupProps.setTopicId(tagId);
            topicPopupProps.setShowTermsPopup(true);
            topicPopupProps.setElementId(tile.element_id ?? 0);
        }
    };

    const toggleCollapseTermSection = (sectionKey: string) => {
        const newCollapseSectionsList = { ...collapseTermSectionList };
        newCollapseSectionsList[sectionKey] = !newCollapseSectionsList?.[sectionKey];
        setCollapseTermSectionList(newCollapseSectionsList);
    };

    if (isLoading) {
        return <Skeleton sx={{ width: '100%', height: '50px' }} />;
    }

    if (!data) return null;

    const tagsSectionKeys = Object.keys(data).filter((key) => key == 'default-topic-type');
    const termsSectionKeys = Object.keys(data)
        .filter((key) => key != 'default-topic-type')
        .sort((a, b) => {
            const termA = data[a].display_order;
            const termB = data[b].display_order;

            return termA - termB;
        });

    return (
        <>
            {tagsSectionKeys.map((sectionKey) => {
                const section = data[sectionKey];
                return (
                    <Stack direction="row" sx={styles.sectionWrapper} key={`info-tag-${sectionKey}-wrapper`}>
                        <Box sx={styles.iconHolder}>
                            <IconHandler icon={{ type: 'mi', value: 'tags' }} sx={{ fontSize: '16px' }} />
                        </Box>
                        <Stack direction="row" flexWrap={'wrap'} sx={{ mt: -0.5, ml: -0.5 }}>
                            {section.topics.map((topic) => {
                                const needToCutLabel = topic.name.length > 30;
                                const label = needToCutLabel ? topic.name.substring(0, 30) + '...' : topic.name;
                                return (
                                    <Box sx={{ mt: 0.5, ml: 0.5 }} key={topic.topic_id}>
                                        <Tooltip
                                            key={`info-tag-tooltip-${topic.topic_id}`}
                                            title={needToCutLabel ? topic.name : ''}
                                        >
                                            <Chip
                                                key={`info-tag-${topic.topic_id}`}
                                                onClick={() => onTagClick(true, topic.topic_id)}
                                                label={label}
                                                color="primary"
                                            />
                                        </Tooltip>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Stack>
                );
            })}

            {termsSectionKeys.length > 0 && (
                <Stack sx={styles.sectionWrapper} width={1} spacing={0.5}>
                    {termsSectionKeys.map((sectionKey) => {
                        const section = data[sectionKey];
                        return (
                            <Box
                                width={1}
                                sx={styles.termSection}
                                className={collapseTermSectionList?.[sectionKey] ? 'collapse' : 'expand'}
                                key={`info-tag-${sectionKey}-wrapper`}
                            >
                                <Stack
                                    direction="row"
                                    className={'term-section__header'}
                                    onClick={() => {
                                        toggleCollapseTermSection(sectionKey);
                                    }}
                                >
                                    <Box sx={styles.iconHolder}>
                                        {section.use_icon === 'fa' && (
                                            <IconHandler
                                                icon={{ type: 'fa', value: section.fa_icon }}
                                                sx={{ fontSize: '14px' }}
                                            />
                                        )}
                                        {section.use_icon === 'uploaded' && (
                                            <IconHandler
                                                icon={{
                                                    type: 'upload',
                                                    value: `data:image/png;base64, ${section.icon}`,
                                                }}
                                            />
                                        )}
                                    </Box>

                                    <Box flexGrow={1}>{section.topic_type_name}</Box>
                                    <Box sx={{ ...styles.iconHolder, ...{ mr: 0, ml: 1 } }}>
                                        <IconMi
                                            icon={
                                                collapseTermSectionList?.[sectionKey] ? 'chevron-right' : 'chevron-down'
                                            }
                                            sx={{ fontSize: 16 }}
                                        />
                                    </Box>
                                </Stack>
                                <Stack
                                    direction="row"
                                    flexWrap={'wrap'}
                                    sx={{ ml: -0.5, pl: '24px' }}
                                    className={'term-section__items'}
                                >
                                    {section.topics.map((topic) => {
                                        const needToCutLabel = topic.name.length > 30;
                                        const label = needToCutLabel ? topic.name.substring(0, 30) + '...' : topic.name;
                                        return (
                                            <Box sx={{ mt: 0.5, ml: 0.5 }} key={topic.topic_id}>
                                                <Tooltip
                                                    key={`info-tag-tooltip-${topic.topic_id}`}
                                                    title={needToCutLabel ? topic.name : ''}
                                                >
                                                    <Chip
                                                        key={`info-tag-${topic.topic_id}`}
                                                        onClick={() => onTagClick(false, topic.topic_id)}
                                                        label={label}
                                                        color="primary"
                                                    />
                                                </Tooltip>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            </Box>
                        );
                    })}
                </Stack>
            )}
        </>
    );
}
